// ── Aviso de solape al asignar fichajes desde "Asignar evento" ────────────
// El admin podía crear un fichaje manual en un día que YA estaba marcado como
// vacaciones, ausencia de día completo, ausencia por horas o compensación.
// Caso real (trabajador #192): los mismos días contaban a la vez como
// vacaciones Y como trabajados.
//
// Esto AVISA, no bloquea: el admin confirma y el fichaje se crea igual. Hay
// casos legítimos (alguien que entra a resolver una urgencia en pleno periodo
// de vacaciones), así que la decisión sigue siendo suya — lo que no puede
// pasar es que lo haga SIN ENTERARSE.
//
// Reglas:
//   · Cuentan los periodos APROBADOS y los PENDIENTES. Los RECHAZADOS no:
//     ya no suman en ningún cálculo del proyecto (ver lib/dayStatus.js).
//   · El día de cada extremo del periodo se resuelve en la zona del
//     TRABAJADOR (lib/workerTime.js), nunca en la del navegador del admin.
//   · El emparejamiento START/END es el de siempre (pairEventsIntoPeriods):
//     aquí NO se reinventa nada de la lógica de días.
//
// Módulo PURO (salvo `loadDayConflicts`, que recibe el fetch inyectado):
// testeado en src/utils/__tests__/dayConflict.test.js.
// Gemelo en la app: TemControlApp/src/lib/dayConflict.js — si se cambia una
// regla aquí, cambiarla allí (no está bajo el guard de check:shared porque
// cada repo lo coloca en su carpeta de lógica pura).

import { EVENT_TYPES } from '../services/api';
import { pairEventsIntoPeriods, isApprovedStatus, isRejectedStatus } from '../lib/dayStatus';
import {
  DEFAULT_TIME_ZONE, dayKeyInZone, dayKeysBetween, addDaysToDayKey, formatHmInZone, isDayKey,
} from '../lib/workerTime';

export const DAY_CONFLICT_KINDS = {
  VACATION: 'vacaciones',
  FULL_DAY: 'diaCompleto',
  HOURLY_ABSENCE: 'ausenciaPorHoras',
  COMPENSATION: 'compensacion',
};

// `hours: true` → el periodo vive dentro de un mismo día y lo que identifica
// al tramo son sus horas, así que el aviso las incluye.
const KIND_DEFS = [
  {
    kind: DAY_CONFLICT_KINDS.VACATION,
    startType: EVENT_TYPES.VACATION_START,
    endType: EVENT_TYPES.VACATION_END,
    label: 'vacaciones',
    hours: false,
  },
  {
    kind: DAY_CONFLICT_KINDS.FULL_DAY,
    startType: EVENT_TYPES.SICK_LEAVE_START,
    endType: EVENT_TYPES.SICK_LEAVE_END,
    label: 'ausencia de día completo',
    hours: false,
  },
  {
    kind: DAY_CONFLICT_KINDS.HOURLY_ABSENCE,
    startType: EVENT_TYPES.ABSENCE_START,
    endType: EVENT_TYPES.ABSENCE_END,
    label: 'ausencia por horas',
    hours: true,
  },
  {
    kind: DAY_CONFLICT_KINDS.COMPENSATION,
    startType: EVENT_TYPES.COMPENSATION_START,
    endType: EVENT_TYPES.COMPENSATION_END,
    label: 'horas compensadas',
    hours: true,
  },
];

// ── Periodos que pisan un día ─────────────────────────────────────────────
// `dayKey` = 'YYYY-MM-DD' del día que el admin quiere fichar.
// `timeZone` = zona del trabajador (timeZoneForWorker), NO la del admin.
export const findDayConflicts = ({ events, dayKey, timeZone = DEFAULT_TIME_ZONE } = {}) => {
  if (!isDayKey(dayKey)) return [];
  const evs = Array.isArray(events) ? events : [];
  if (!evs.length) return [];

  const out = [];
  for (const def of KIND_DEFS) {
    for (const p of pairEventsIntoPeriods(evs, def.startType, def.endType)) {
      if (isRejectedStatus(p.approvalStatus)) continue;
      const from = dayKeyInZone(p.start, timeZone);
      const to = dayKeyInZone(p.end, timeZone);
      if (!from || !to) continue;
      if (!dayKeysBetween(from, to).includes(dayKey)) continue;
      out.push({
        kind: def.kind,
        label: def.label,
        approved: isApprovedStatus(p.approvalStatus),
        from,
        to,
        // Solo para los tramos horarios; en un periodo abierto (START huérfano)
        // no hay hora de fin que enseñar.
        startHm: def.hours ? formatHmInZone(p.start, timeZone) : null,
        endHm: def.hours && p.endEvent ? formatHmInZone(p.end, timeZone) : null,
      });
    }
  }
  return out;
};

// ── Texto del aviso ───────────────────────────────────────────────────────
const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// 'YYYY-MM-DD' → partes numéricas SIN pasar por Date: una clave de día no es
// un instante y no se puede "mover" de zona.
const keyParts = (key) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(key || ''));
  return m ? { y: Number(m[1]), mo: Number(m[2]), d: Number(m[3]) } : null;
};

// "el 4 de agosto" / "del 4 al 7 de agosto" / "del 4 de agosto al 2 de
// septiembre" / "del 30 de diciembre de 2026 al 5 de enero de 2027".
// El año solo aparece cuando el periodo cruza el fin de año: en cualquier otro
// caso es el del día que se está fichando y solo añade ruido.
export const formatConflictRange = (from, to) => {
  const a = keyParts(from);
  const b = keyParts(to);
  if (!a || !b) return '';
  const day = (p, withYear) => `${p.d} de ${MONTHS[p.mo - 1]}${withYear ? ` de ${p.y}` : ''}`;
  if (from === to) return `el ${day(a, false)}`;
  if (a.y !== b.y) return `del ${day(a, true)} al ${day(b, true)}`;
  if (a.mo === b.mo) return `del ${a.d} al ${day(b, false)}`;
  return `del ${day(a, false)} al ${day(b, false)}`;
};

const describeConflict = (c, first) => {
  const range = formatConflictRange(c.from, c.to);
  const hours = c.startHm && c.endHm ? ` (de ${c.startHm} a ${c.endHm})` : '';
  const pending = c.approved ? '' : ' (pendiente de aprobar)';
  const head = first ? 'Este día está marcado como' : 'Además, está marcado como';
  return `${head} ${c.label}${range ? ` ${range}` : ''}${hours}${pending}.`;
};

// Aviso completo para la confirmación (window.confirm en la web, Alert en la
// app). null si el día está limpio: sin aviso no se pregunta nada.
export const dayConflictWarning = (conflicts, { action = 'añadir un fichaje' } = {}) => {
  const list = (Array.isArray(conflicts) ? conflicts : []).filter(Boolean);
  if (!list.length) return null;
  const body = list.map((c, i) => describeConflict(c, i === 0)).join(' ');
  return `${body} ¿Seguro que quieres ${action}?`;
};

// ── Carga de los eventos necesarios ───────────────────────────────────────
// Ventana a consultar alrededor del día. NO basta con pedir el propio día: un
// periodo de vacaciones del 1 al 30 tiene su VACATION_START fuera de ese día y
// sin el START no hay par que emparejar (pairEventsIntoPeriods descarta los
// END huérfanos). 45 días a cada lado cubren de sobra cualquier periodo real.
export const DAY_CONFLICT_WINDOW_DAYS = 45;

export const dayConflictWindow = (dayKey, days = DAY_CONFLICT_WINDOW_DAYS) => ({
  from: addDaysToDayKey(dayKey, -days),
  to: addDaysToDayKey(dayKey, days),
});

// `fetchEvents(from, to)` → promesa con los eventos del trabajador.
// `fallbackEvents` → los que la pantalla ya tuviera cargados, por si el fetch
// falla (vienen recortados a la ventana del histórico, así que pueden perder
// el extremo de un periodo: son el plan B, no el plan A).
//
// NUNCA lanza: si no se puede comprobar, se devuelve [] y el flujo sigue sin
// aviso. Un fallo de red no puede impedir que el admin asigne un fichaje.
export const loadDayConflicts = async ({
  fetchEvents, dayKey, timeZone = DEFAULT_TIME_ZONE, fallbackEvents = null,
} = {}) => {
  if (!isDayKey(dayKey)) return [];
  let events = null;
  if (typeof fetchEvents === 'function') {
    const { from, to } = dayConflictWindow(dayKey);
    if (from && to) {
      try {
        const res = await fetchEvents(from, to);
        if (Array.isArray(res)) events = res;
      } catch {
        events = null;
      }
    }
  }
  if (!events && Array.isArray(fallbackEvents)) events = fallbackEvents;
  if (!events) return [];
  try {
    return findDayConflicts({ events, dayKey, timeZone });
  } catch {
    return [];
  }
};
